import type { ExamCourse } from '@exam-management/examination-course-list';
import {
  SearchOwnedAndAssignedTasksDocument,
  useCreateTaskAndSubtasksMutation,
} from '@exam-management/graphql';
import { useNavigate } from '@exam-management/router';
import { useAuth } from '@shared/auth';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { createContext, useMemo, useState } from 'react';

import { REVIEW_QSET_TASK_CREATION_SEQUENCES } from '../../constants';
import type { QSetReviewTaskCreationFormInput } from '../../schemas';

import { createTaskAndSubtasksMapper } from './mapper';

interface QSetReviewTaskCreationContextData {
  step: number;
  examinationCourse: ExamCourse | undefined;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  resetStep: () => void;
  selectExaminationCourse: (selectedExaminationCourse: ExamCourse) => void;
  createQSetReviewTask: (data: QSetReviewTaskCreationFormInput) => Promise<void>;
}

export const QSetReviewTaskCreationContext = createContext<
  QSetReviewTaskCreationContextData | undefined
>(undefined);

export default function QSetReviewTaskCreationProvider(props: Readonly<PropsWithChildren>) {
  const { children } = props;

  const [step, setStep] = useState(0);
  const [examinationCourse, setExaminationCourse] = useState<ExamCourse>();
  const navigate = useNavigate();

  const { userProfile } = useAuth();

  const { showSnackbar } = useSnackbar();

  const [createTaskAndSubtasks] = useCreateTaskAndSubtasksMutation({
    onError: () => {
      showSnackbar({ message: 'Failed to create Task.', variant: 'error' });
    },
    onCompleted: (data) => {
      showSnackbar({ message: 'Task created successfully.', variant: 'success' });
      navigate('/task-management/qset-review/:taskId', {
        params: { taskId: data.wflCreateTaskAndSubtasks.task.id },
      });
    },
    refetchQueries: [SearchOwnedAndAssignedTasksDocument],
    awaitRefetchQueries: true,
  });

  const goToNextStep = () => {
    if (step < REVIEW_QSET_TASK_CREATION_SEQUENCES.length - 1) {
      setStep((prevStep) => prevStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (step > 0) {
      setStep((prevStep) => prevStep - 1);
    }
  };

  const resetStep = () => {
    setStep(0);
    setExaminationCourse(undefined);
  };

  const selectExaminationCourse = (selectedExaminationCourse: ExamCourse) => {
    setExaminationCourse(selectedExaminationCourse);
  };

  const handleCreateQSetReviewTask = async (data: QSetReviewTaskCreationFormInput) => {
    await createTaskAndSubtasks({
      variables: {
        createTaskAndSubtasksInput: createTaskAndSubtasksMapper(data, userProfile?.id ?? ''),
      },
    });
  };

  const contextValue = useMemo(() => {
    return {
      step: step,
      examinationCourse: examinationCourse,
      goToNextStep: goToNextStep,
      goToPreviousStep: goToPreviousStep,
      resetStep: resetStep,
      selectExaminationCourse: selectExaminationCourse,
      createQSetReviewTask: handleCreateQSetReviewTask,
    };
  }, [
    step,
    examinationCourse,
    goToNextStep,
    goToPreviousStep,
    resetStep,
    selectExaminationCourse,
    handleCreateQSetReviewTask,
  ]);

  return (
    <QSetReviewTaskCreationContext.Provider value={contextValue}>
      {children}
    </QSetReviewTaskCreationContext.Provider>
  );
}
